import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { chargingStationsQueryOptions } from '@/api/cpms';
import { meteringPointSearchQueryOptions } from '@/api/oda';
import { resolveAccessLevel, useIamUser } from '@/auth/iam';
import { useIsAuthenticated } from '@/auth/useIsAuthenticated';
import { NAV_ITEMS } from '@/layouts/nav';
import { matchScore } from '@/modules/topbar/utils/searchMatch';

/** Below this the palette shows a hint instead of firing any request. */
export const MIN_QUERY_LENGTH = 2;

/** Suggestions kept per group — the palette is a jump list, not a table. */
const MAX_PER_GROUP = 5;

/**
 * Station ids packed into a location's combined-analytics link. A location with
 * more stations than this opens with its first `MAX_COMBINED_STATIONS` (ordered
 * as CPMS returns them) rather than an over-long URL; the dashboard table is the
 * place to work with the full set.
 */
const MAX_COMBINED_STATIONS = 40;

const SEARCH_GROUPS = ['pages', 'stations', 'locations', 'meteringPoints'] as const;
export type SearchGroup = (typeof SEARCH_GROUPS)[number];

export interface SearchResult {
  /** Stable across renders — also the DOM id suffix of the option. */
  id: string;
  group: SearchGroup;
  title: string;
  subtitle?: string;
  /** Router path the result navigates to. */
  to: string;
  score: number;
}

export interface SearchResultGroup {
  group: SearchGroup;
  items: SearchResult[];
}

function rank(results: SearchResult[]): SearchResult[] {
  return results
    .sort((a, b) => b.score - a.score || a.title.localeCompare(b.title))
    .slice(0, MAX_PER_GROUP);
}

/**
 * The global search palette's result set: app destinations, CPMS charging
 * stations and their locations, plus ODA metering points for domain callers.
 *
 * Nothing is fetched until the query is long enough — the topbar is mounted on
 * every screen, and the fleet download is not something a page load should pay
 * for. Once it does run it reads the shared `['cpms','charging-stations']` cache
 * entry, so on the CPO screens the palette costs nothing at all.
 *
 * `query` is expected to be debounced by the caller.
 */
export function useGlobalSearch(query: string) {
  const { t } = useTranslation();
  const trimmed = query.trim();
  const isActive = trimmed.length >= MIN_QUERY_LENGTH;

  const isAuthenticated = useIsAuthenticated();
  const { data: iamUser } = useIamUser();
  // ODA data is global, so the backend serves domain callers only — matching the
  // sidebar, which hides the ODA entry from everyone else.
  const isDomain = iamUser ? resolveAccessLevel(iamUser.user).level === 'domain' : false;

  const {
    data: stations,
    isFetching: isFetchingStations,
    isError: isStationsError,
  } = useQuery({
    ...chargingStationsQueryOptions(),
    enabled: isActive && isAuthenticated,
  });

  const {
    data: meteringPoints,
    isFetching: isFetchingMeteringPoints,
    isError: isMeteringPointsError,
  } = useQuery({
    ...meteringPointSearchQueryOptions(trimmed),
    enabled: isActive && isAuthenticated && isDomain,
  });

  const pages = useMemo<SearchResult[]>(() => {
    if (!isActive) return [];
    const results = NAV_ITEMS.flatMap((section) =>
      section.items
        .filter((item) => item.href && (!item.domainOnly || isDomain))
        .flatMap((item) => {
          const title = t(item.labelKey);
          const sectionLabel = t(section.labelKey);
          const score = matchScore(trimmed, [title, sectionLabel]);
          if (score === null) return [];
          return [
            {
              id: `page:${item.id}`,
              group: 'pages' as const,
              title,
              subtitle: sectionLabel,
              to: item.href ?? '/',
              score,
            },
          ];
        }),
    );
    return rank(results);
  }, [isActive, isDomain, t, trimmed]);

  const stationResults = useMemo<SearchResult[]>(() => {
    if (!isActive || !stations) return [];
    const results = stations.flatMap((station) => {
      const score = matchScore(trimmed, [
        station.customIdentifier,
        station.id,
        station.locationName,
      ]);
      if (score === null) return [];
      return [
        {
          id: `station:${station.id}`,
          group: 'stations' as const,
          title: station.customIdentifier || station.id,
          ...(station.locationName ? { subtitle: station.locationName } : {}),
          to: `/cpo-analytics/${encodeURIComponent(station.id)}`,
          score,
        },
      ];
    });
    return rank(results);
  }, [isActive, stations, trimmed]);

  const locationResults = useMemo<SearchResult[]>(() => {
    if (!isActive || !stations) return [];
    // CPMS has a locations endpoint, but every location that can be *acted on*
    // from here is one that has stations — so the groups come from the fleet
    // already in hand instead of a second download.
    const byLocation = new Map<string, { name: string; stationIds: string[] }>();
    for (const station of stations) {
      if (!station.locationName) continue;
      const key = station.locationId || station.locationName;
      const entry = byLocation.get(key);
      if (entry) entry.stationIds.push(station.id);
      else byLocation.set(key, { name: station.locationName, stationIds: [station.id] });
    }

    const results = [...byLocation.entries()].flatMap(([key, location]) => {
      const score = matchScore(trimmed, [location.name]);
      if (score === null) return [];
      const params = new URLSearchParams();
      for (const id of location.stationIds.slice(0, MAX_COMBINED_STATIONS)) {
        params.append('id', id);
      }
      return [
        {
          id: `location:${key}`,
          group: 'locations' as const,
          title: location.name,
          subtitle: t('topbar.search.stationCount', { count: location.stationIds.length }),
          // The combined screen redirects a single id to that station's own screen.
          to: `/cpo-analytics/combined?${params}`,
          score,
        },
      ];
    });
    return rank(results);
  }, [isActive, stations, t, trimmed]);

  const meteringPointResults = useMemo<SearchResult[]>(() => {
    if (!isActive || !meteringPoints) return [];
    // Already matched server-side; the score only orders them against each other.
    const results = meteringPoints.flatMap((point) => {
      const score = matchScore(trimmed, [point.eanId, point.meterId, point.locationName]) ?? 1;
      const subtitle = point.locationName ?? point.meterId;
      return [
        {
          id: `metering-point:${point.eanId}`,
          group: 'meteringPoints' as const,
          title: point.eanId,
          ...(subtitle ? { subtitle } : {}),
          to: `/oda/${encodeURIComponent(point.eanId)}`,
          score,
        },
      ];
    });
    return rank(results);
  }, [isActive, meteringPoints, trimmed]);

  const groups = useMemo<SearchResultGroup[]>(
    () =>
      (
        [
          { group: 'pages' as const, items: pages },
          { group: 'stations' as const, items: stationResults },
          { group: 'locations' as const, items: locationResults },
          { group: 'meteringPoints' as const, items: meteringPointResults },
        ] satisfies SearchResultGroup[]
      ).filter((entry) => entry.items.length > 0),
    [locationResults, meteringPointResults, pages, stationResults],
  );

  // Flat, in render order — the arrow keys walk this list.
  const results = useMemo(() => groups.flatMap((entry) => entry.items), [groups]);

  return {
    groups,
    results,
    isActive,
    isFetching: isActive && (isFetchingStations || isFetchingMeteringPoints),
    // A failed source, so the palette can say so — page results are local and
    // still render, but "no matches" must not stand in for "couldn't search".
    isError: isActive && (isStationsError || isMeteringPointsError),
  };
}
